<template>
  <FormFieldwrap>
    <select v-model="value" :name="name" v-bind="$attrs" :class="$style.base" @input="onChange">
      <option disabled value="">
        bitte auswählen
      </option>
      <option v-for="(item, i) in options" :key="i" :value="item">
        {{ item }}
      </option>
    </select>
    <BaseLabel>{{ label }}</BaseLabel>
  </FormFieldwrap>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate'
import type { Schema } from 'yup'
import { mixed } from 'yup'
import { computed } from 'vue'
import FormFieldwrap from '~/base/components/form/FormFieldwrap.vue'
import BaseLabel from '~/base/components/label/Label.vue'

interface SelectProps {
  modelValue: string
  options: string[]
  label: string
  name: string
  rules?: Schema
  required?: boolean
}

const props = defineProps<SelectProps>()
const emits = defineEmits(['update:modelValue'])
const adjustedRules = computed(() => {
  return props.required
    ? props.rules
      ? props.rules
      : mixed().oneOf(props.options, 'Dieses Feld muss ausgefüllt sein')
    : props.rules
})

const { value, handleChange } = useField(props.name, adjustedRules, {
  initialValue: props.modelValue
})

const onChange = (event: Event) => {
  handleChange(event, true)
  emits('update:modelValue', value.value)
}
</script>

<style module>
.base {
  display: block;
  width: inherit;
  font-size: clamp(16px, 2vw, 18px);
  line-height: clamp(20px, 2vw, 22px);
  border-radius: var(--radius-default);
  border: 1px solid var(--color-grey3);
  transition: var(--transition-default);
  padding: 26px 12px 7px 12px;
  position: relative;
  background-color: var(--color-white);
  background-repeat: no-repeat;
  background-position: right 0.7em top 70%;
  background-size: 1em auto;

  &:not([multiple]):not([size]) {
    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8' standalone='no'%3F%3E%3Csvg xmlns:dc='http://purl.org/dc/elements/1.1/' xmlns:cc='http://creativecommons.org/ns%23' xmlns:rdf='http://www.w3.org/1999/02/22-rdf-syntax-ns%23' xmlns:svg='http://www.w3.org/2000/svg' xmlns='http://www.w3.org/2000/svg' id='svg2' viewBox='0 0 33.584946 20' height='20' width='33.584946' version='1.1'%3E%3Cmetadata id='metadata13'%3E%3Crdf:RDF%3E%3Ccc:Work rdf:about=''%3E%3Cdc:format%3Eimage/svg+xml%3C/dc:format%3E%3Cdc:type rdf:resource='http://purl.org/dc/dcmitype/StillImage' /%3E%3Cdc:title%3E%3C/dc:title%3E%3C/cc:Work%3E%3C/rdf:RDF%3E%3C/metadata%3E%3Cdefs id='defs11' /%3E%3Cg transform='translate(207.1015,-796.8985)' id='icomoon-ignore' /%3E%3Cpath style='fill:%23454f63;fill-opacity:1' id='path7' d='M 0,2.6415 2.641502,0 16.792474,14.717 30.943445,0 33.584947,2.6415 16.792474,20 Z' /%3E%3C/svg%3E%0A");
  }
}

:focus {
  border-color: var(--color-pacificblue);
  outline: 0;
}

.invalid {
  border-color: var(--color-alert);
  color: var(--color-alert);

  :focus {
    border-color: var(--color-alert);
    color: var(--color-alert);
  }
}
</style>
